import { useState, useEffect, useCallback, useRef } from 'react';

export function useInstallments(formData, parseAmountToNumber, formatDateForInput) {
  const buildSchedule = useCallback(() => {
    if (!formData.hasInstallments) {
      return [];
    }

    const totalAmount = parseAmountToNumber(formData.amount || '0');
    const remainingAmount = Math.max(totalAmount, 0);
    const installmentsCount = Math.max(1, formData.installments || 1);
    const baseDate = formData.date
      ? (() => { const [y, m, d] = formData.date.split('-').map(Number); return new Date(y, m - 1, d); })()
      : new Date();

    const schedule = [];
    const remainingCents = Math.round(remainingAmount * 100);
    const baseCents = Math.floor(remainingCents / installmentsCount);
    let remainder = remainingCents - baseCents * installmentsCount;

    for (let i = 0; i < installmentsCount; i++) {
      let cents = baseCents;
      if (remainder > 0) {
        cents += 1;
        remainder -= 1;
      }
      const installmentDate = new Date(baseDate);
      installmentDate.setMonth(installmentDate.getMonth() + i);
      schedule.push({
        installmentNumber: i + 1,
        amount: (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        date: formatDateForInput(installmentDate),
        paymentMethod: formData.installmentPaymentMethod || 'cartao_credito',
        paymentStatus: 'pendente'
      });
    }

    return schedule;
  }, [
    formData.hasInstallments,
    formData.amount,
    formData.installments,
    formData.date,
    formData.installmentPaymentMethod,
    parseAmountToNumber,
    formatDateForInput
  ]);

  const [installmentSchedule, setInstallmentSchedule] = useState([]);
  const [isScheduleLocked, setIsScheduleLocked] = useState(false);
  // Ref para leitura síncrona do lock — evita problema de timing de efeito/render
  const isLockedRef = useRef(false);

  useEffect(() => {
    if (!formData.hasInstallments) {
      setInstallmentSchedule([]);
      setIsScheduleLocked(false);
      isLockedRef.current = false;
      return;
    }

    if (!isLockedRef.current) {
      setInstallmentSchedule(buildSchedule());
    }
  }, [formData.hasInstallments, buildSchedule, isScheduleLocked]);

  const handleInstallmentFieldChange = (index, field, value) => {
    setInstallmentSchedule((prev) => {
      const updated = [...prev];
      if (updated[index]?.paymentStatus === 'pago') {
        return prev;
      }
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    isLockedRef.current = true;
    setIsScheduleLocked(true);
  };

  const recalculateInstallments = () => {
    isLockedRef.current = false;
    setIsScheduleLocked(false);
    setInstallmentSchedule(buildSchedule());
  };

  const unlockSchedule = () => {
    isLockedRef.current = false;
    setIsScheduleLocked(false);
  };

  const overrideSchedule = (entries = []) => {
    isLockedRef.current = true;
    setIsScheduleLocked(true);
    setInstallmentSchedule(entries);
  };

  return {
    installmentSchedule,
    handleInstallmentFieldChange,
    recalculateInstallments,
    unlockSchedule,
    overrideSchedule
  };
}
